/**
 * Administration > Access.
 *
 * /admin/access — review and grant per-tool access to Cockpit. One row per
 * person; their tools render as Pills (e.g. "Orders · Viewer"), or a single
 * "All tools · Admin" pill for superadmins. "+ Grant access" opens the editor
 * for a new person (directory picker); "Edit" opens it prefilled for an
 * existing grant.
 *
 * Gated by the access.admin capability. Any signed-in user who lacks it sees
 * an access-denied card. Mirrors the compliance CoAs list scaffold.
 *
 * Tool filter (2026-08-10): the toolbar carries a searchable multi-select
 * (`SearchableSelect`, the shared >15-option picker per COCKPIT.md 2.8) over
 * every grantable tool, labeled "Section: Tool". Selecting tools narrows the
 * list to people holding ALL of them (AND semantics; see
 * `matchesToolFilter` in accessScopes.ts), super-admins always match since
 * super-admin bypasses per-tool grants. The selection lives in the URL as a
 * repeated `tool` param (same pattern as `store_no` on Item Sales), so a
 * filtered view is a shareable link, and renders as removable Pill chips
 * with a clear-all link once at least one is picked.
 *
 * Mobile card (2026-08-10, Steven's screenshot feedback): below `md` this
 * page opts into the shell's `renderCards` (COCKPIT.md 2.7), the second
 * consumer after Item Sales. The default mobile STACKED table put each of
 * Person / Access / Edit on its own labelled block with large gaps, and a
 * many-tool person's pill list pushed Edit off the bottom of the card. The
 * hand-built card instead pins Edit to the top-right corner (always visible,
 * no scrolling needed), puts name + email on one line, and caps the pill
 * list at 6 with a "+N more" toggle (super-admin renders as its one badge,
 * never a pill list).
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { SearchableSelect, type SearchableSelectOption } from '@/components/SearchableSelect';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import { useReportPageState } from '@/lib/reportPageState';
import {
  hasAccessAdmin,
  useAccessGrants,
  type AccessGrant,
} from '@/lib/useAccess';
import { buildGrantableDepts, matchesToolFilter, slugLabel } from '@/lib/accessScopes';
import { GrantEditor, AddUserForm } from './GrantEditor';

// Options for the tool filter: one per grantable accessSlug (the Everyone
// rows have nothing to grant, so filtering by one would never narrow
// anything). Built once; the catalog does not change at runtime.
const TOOL_FILTER_OPTIONS: SearchableSelectOption[] = buildGrantableDepts().flatMap((dept) =>
  dept.entries
    .filter((e) => e.grantable)
    .map((e) => ({ value: e.accessSlug, label: `${dept.deptName}: ${e.toolName}` })),
);

function personName(g: AccessGrant): string {
  return g.name ?? g.email ?? g.principal_ref;
}

function sortGrants(rows: AccessGrant[], dir: SortDir): AccessGrant[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = personName(a).toLowerCase();
    const bv = personName(b).toLowerCase();
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
}

export function AccessConsole() {
  const { user } = useSession();
  const canView = hasAccessAdmin(user?.permissions, user?.superAdmin);

  const grantsQ = useAccessGrants();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortDir | null>('asc');
  const [editor, setEditor] = useState<
    { mode: 'new' } | { mode: 'edit'; grant: AccessGrant } | null
  >(null);
  const [addingUser, setAddingUser] = useState(false);

  // Tool filter selection lives in the URL as a repeated `tool` param (same
  // pattern as `store_no` on Item Sales), so a filtered view is a shareable
  // link.
  const [searchParams, setSearchParams] = useSearchParams();
  const toolSel = useMemo(() => searchParams.getAll('tool'), [searchParams]);
  const setToolSel = useCallback(
    (next: string[]) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.delete('tool');
          for (const slug of next) nextParams.append('tool', slug);
          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const removeTool = (slug: string) => setToolSel(toolSel.filter((s) => s !== slug));
  const clearTools = () => setToolSel([]);

  const toggleSort = () =>
    setSort((prev) => (prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'));

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = grantsQ.grants.filter((g) => {
      const hay = `${g.name ?? ''} ${g.email ?? ''}`.toLowerCase();
      if (needle && !hay.includes(needle)) return false;
      return matchesToolFilter(g, toolSel);
    });
    return sort ? sortGrants(filtered, sort) : filtered;
  }, [grantsQ.grants, search, sort, toolSel]);

  // Human-readable page state for Support > Report an issue (ISD-357): the
  // tool filter and search text, in the same "Section: Tool" words the pill
  // chips already show, so a ticket does not carry raw accessSlug strings.
  // Only published when the operator has actually set one, same as the tool
  // filter's own `activeFilterCount`.
  const pageStateEntries = useMemo(() => {
    const entries: { label: string; value: string }[] = [];
    if (toolSel.length > 0) entries.push({ label: 'Tools', value: toolSel.map(slugLabel).join(', ') });
    const trimmedSearch = search.trim();
    if (trimmedSearch) entries.push({ label: 'Search', value: trimmedSearch });
    return entries;
  }, [toolSel, search]);
  useReportPageState(pageStateEntries);

  if (user && !canView) return <AccessDeniedCard />;

  const hasFilters = Boolean(search.trim()) || toolSel.length > 0;
  // Each filter CONTROL counts once, not once per selected value inside it:
  // the tool multi-select counts as 1 no matter how many tools are picked.
  const activeFilterCount = (search.trim() ? 1 : 0) + (toolSel.length > 0 ? 1 : 0);

  const toolbar = (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px] max-w-md">
          <label
            htmlFor="access-search"
            className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1"
          >
            Search people
          </label>
          <input
            id="access-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name or email..."
            className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
          />
        </div>
        <div className="min-w-[220px]">
          <span className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
            Filter by tool
          </span>
          <SearchableSelect
            options={TOOL_FILTER_OPTIONS}
            selected={toolSel}
            onChange={setToolSel}
            placeholder="All tools"
            ariaLabel="Filter by tool"
            emptyMessage="No tools match."
          />
        </div>
        <button
          type="button"
          onClick={() => setAddingUser(true)}
          className="inline-flex items-center gap-1 text-sm rounded border border-amp-line px-3 py-1.5 font-medium text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          + Add user
        </button>
        <button
          type="button"
          onClick={() => setEditor({ mode: 'new' })}
          className="inline-flex items-center gap-1 text-sm rounded bg-amp-olive-deep px-3 py-1.5 font-medium text-amp-cream hover:bg-amp-olive dark:bg-amp-olive dark:hover:bg-amp-olive-deep"
        >
          + Grant access
        </button>
      </div>
      {toolSel.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {toolSel.map((slug) => (
            <Pill key={slug} variant="selected">
              {slugLabel(slug)}
              <button
                type="button"
                onClick={() => removeTool(slug)}
                aria-label={`Remove ${slugLabel(slug)} filter`}
                className="hover:opacity-80"
              >
                <X className="h-3 w-3" />
              </button>
            </Pill>
          ))}
          <button
            type="button"
            onClick={clearTools}
            className="text-xs text-amp-olive hover:underline dark:text-amp-olive-dark"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0">

      <div className="flex flex-wrap items-end justify-between mb-6 gap-3 shrink-0">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            Administration · Access
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Access</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Review who can reach each Cockpit tool, and grant or change access.
          </p>
        </div>
      </div>

      {/* Guest-access explainer. Muted info callout (matches the GrantEditor
          note token style), static and non-dismissible, above the grid. */}
      <div className="mb-6 shrink-0 max-w-3xl rounded-lg border border-amp-line bg-amp-cream/40 px-4 py-3 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm/40">
        <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">
          How to give access to someone outside Ampersand
        </h2>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          External people such as board members, vendors, and partners sign in as guests. To set
          one up: IT first invites them as a guest in Microsoft using their email, then you search
          for them here by email and check the tools they should see. They sign in at the Cockpit
          login page with Sign in with Microsoft and get a one-time code by email. Anyone with an
          @ampersandbrands.com account is already a member and does not need an invite.
        </p>
      </div>

      <DataGridShell
        total={rows.length}
        countNoun="people"
        toolbar={toolbar}
        activeFilterCount={activeFilterCount}
        renderCards={() => (
          <AccessCards
            isLoading={grantsQ.isLoading}
            error={grantsQ.error}
            rows={rows}
            hasFilters={hasFilters}
            onEdit={(g) => setEditor({ mode: 'edit', grant: g })}
          />
        )}
        footer={
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Changes take effect at the user's next sign-in. All changes are logged.
          </p>
        }
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader
                label="Person"
                sorted={sort}
                onToggle={toggleSort}
                thClassName="px-3 py-2"
              />
              <th className="text-left font-medium px-3 py-2">Access</th>
              <th className="text-right font-medium px-3 py-2">
                <span className="sr-only">Edit</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {grantsQ.isLoading ? (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading access...
                </td>
              </tr>
            ) : grantsQ.error ? (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-red-700 dark:text-red-300">
                  Could not load: {grantsQ.error.message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  {hasFilters ? 'No people match these filters.' : 'No access grants yet.'}
                </td>
              </tr>
            ) : (
              rows.map((g) => (
                <GrantRow key={g.principal_ref} grant={g} onEdit={() => setEditor({ mode: 'edit', grant: g })} />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>

      {editor && (
        <GrantEditor
          mode={editor.mode}
          grant={editor.mode === 'edit' ? editor.grant : undefined}
          onClose={() => setEditor(null)}
        />
      )}

      {addingUser && <AddUserForm onClose={() => setAddingUser(false)} />}
    </div>
  );
}

function GrantRow({ grant, onEdit }: { grant: AccessGrant; onEdit: () => void }) {
  return (
    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm align-top">
      <td className="px-3 py-2">
        <div className="text-amp-ink dark:text-amp-dark-ink">{grant.name ?? grant.email ?? grant.principal_ref}</div>
        {grant.email && grant.name && (
          <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{grant.email}</div>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1.5">
          {grant.super_admin ? (
            <Pill>Super admin</Pill>
          ) : grant.tools.length === 0 ? (
            <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">No access</span>
          ) : (
            grant.tools.map((slug) => <Pill key={slug}>{slugLabel(slug)}</Pill>)
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Edit
        </button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Mobile card mode (COCKPIT.md 2.7). Second consumer after Item Sales.
// ---------------------------------------------------------------------------

/** How many tool pills show before collapsing behind a "+N more" toggle. */
const PILL_PREVIEW_COUNT = 6;

function AccessCards({
  isLoading,
  error,
  rows,
  hasFilters,
  onEdit,
}: {
  isLoading: boolean;
  error: Error | null;
  rows: AccessGrant[];
  hasFilters: boolean;
  onEdit: (grant: AccessGrant) => void;
}) {
  if (isLoading) return <CardNotice message="Loading access..." />;
  if (error) return <CardNotice message={`Could not load: ${error.message}`} />;
  if (rows.length === 0) {
    return (
      <CardNotice message={hasFilters ? 'No people match these filters.' : 'No access grants yet.'} />
    );
  }
  return (
    <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
      {rows.map((g) => (
        <li key={g.principal_ref}>
          <PersonCard grant={g} onEdit={() => onEdit(g)} />
        </li>
      ))}
    </ul>
  );
}

/**
 * One person, one card. Edit is pinned to the top-right corner so it is
 * always reachable without scrolling past a long pill list; the identity
 * line and the pill block both carry `pr-16` to stay clear of it. A
 * many-tool grant shows the first 6 pills plus a "+N more" toggle that
 * expands in place, rather than pushing Edit further down the page.
 */
function PersonCard({ grant, onEdit }: { grant: AccessGrant; onEdit: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const email = grant.email && grant.name ? grant.email : null;
  const visibleTools = expanded ? grant.tools : grant.tools.slice(0, PILL_PREVIEW_COUNT);
  const hiddenCount = grant.tools.length - visibleTools.length;

  return (
    <div className="relative px-3 py-3">
      <button
        type="button"
        onClick={onEdit}
        className="absolute right-3 top-3 text-xs border border-amp-line rounded px-2.5 py-1 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
      >
        Edit
      </button>
      <div className="flex items-baseline gap-2 pr-16">
        <span className="truncate text-[14px] font-semibold text-amp-ink dark:text-amp-dark-ink">
          {personName(grant)}
        </span>
        {email && (
          <span className="min-w-0 truncate text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {email}
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 pr-16">
        {grant.super_admin ? (
          <Pill>Super admin</Pill>
        ) : grant.tools.length === 0 ? (
          <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">No access</span>
        ) : (
          <>
            {visibleTools.map((slug) => (
              <Pill key={slug}>{slugLabel(slug)}</Pill>
            ))}
            {hiddenCount > 0 ? (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="text-xs text-amp-olive hover:underline dark:text-amp-olive-dark"
              >
                +{hiddenCount} more
              </button>
            ) : expanded && grant.tools.length > PILL_PREVIEW_COUNT ? (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="text-xs text-amp-olive hover:underline dark:text-amp-olive-dark"
              >
                Show less
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/** Loading / error / empty copy in card mode, where there is no table row to fill. */
function CardNotice({ message }: { message: string }) {
  return <p className="px-3 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{message}</p>;
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        The Access console is gated by the access.admin capability. Ask IT to grant you access.
      </p>
    </div>
  );
}
